import { ChainTokens, TokenSymbol } from '@ui/constants/tokens/tokenSymbols';
import { ChainId, ChainName } from '@ui/types/chain';
import { Address } from 'viem';

export const FALLBACK_ADDRESS =
  '0x0000000000000000000000000000000000000000' as const;

/** every ERC20 token must have address */
type ERC20TokenAddressMap<Symbol extends string> = Record<Symbol, Address>;
type TokenAddressByChain = {
  [ChainId.MONAD_MAINNET]: ERC20TokenAddressMap<ChainTokens>;
  [ChainId.MONAD_TESTNET]: ERC20TokenAddressMap<ChainTokens>;
};

export const TOKEN_ADDRESS_BY_CHAIN = {
  [ChainId.MONAD_MAINNET]: {
    USDC: '0x754704Bc059F8C67012fEd69BC8A327a5aafb603',
    WMON: '0x3bd359C1119dA7Da1D913D1C4D2B7c461115433A',
    WRAPPED_NATIVE: '0x3bd359C1119dA7Da1D913D1C4D2B7c461115433A',
    LVUSD: '0xFD44B35139Ae53FFF7d8F2A9869c503D987f00d1',
    LVMON: '0x91b81bfbe3A747230F0529Aa28d8b2Bc898E6D56',
    sLVMON: '0x61b29EfEf2E6f866bA4AaeFDb87d2837C6a22b9c',
    LV: '0x1001fF13bf368Aa4fa85F21043648079F00E1001',
    Rona: FALLBACK_ADDRESS,
    EMO: '0x81A224F8A62f52BdE942dBF23A56df77A10b7777',
    xLV: '0x1001fF13bf368Aa4fa85F21043648079F00E1001',
    sxLV: '0x1001fF13bf368Aa4fa85F21043648079F00E1001',
    yLV: '0x1001fF13bf368Aa4fa85F21043648079F00E1001',
  },
  [ChainId.MONAD_TESTNET]: {
    USDC: '0xA1E873dFc5B1b69e268ab88de42E6e2090bc545a',
    WMON: '0xFb8bf4c1CC7a94c73D209a149eA2AbEa852BC541',
    WRAPPED_NATIVE: '0xFb8bf4c1CC7a94c73D209a149eA2AbEa852BC541',
    LVUSD: '0x76903262B1d4b70E3F2a5Af8Ed9F7E8Ace646Dcc',
    LVMON: '0xBe3fa50514D9617ce645a02B34F595541AF02b6b',
    sLVMON: '0xA640f31B0A562411FBEAcE363970F9567Af06311',
    LV: '0x10017739644B8980D797dFBC6B61E9Bca9061001',
    Rona: '0xe099B47899BB851D4df6392F7A7B242600aa7777',
    EMO: FALLBACK_ADDRESS,
    xLV: '0x10017739644B8980D797dFBC6B61E9Bca9061001',
    sxLV: '0x10017739644B8980D797dFBC6B61E9Bca9061001',
    yLV: '0x10017739644B8980D797dFBC6B61E9Bca9061001',
  },
} satisfies TokenAddressByChain;

const commonContract = [
  'MultiCall3',
  'LeverUp',
  'GenesisAirdrop',
  'Pyth',
  'OneClickAgent',
  'GeneralGenesisPoints',
  'DegenGenesisPoints',
  'DexAggregator',
  'LVMONRedeemer',
  'Launchpad',
  'LaunchpadUIHelper',
] as const;
const contractsByChain = {
  [ChainName.MONAD_TESTNET]: commonContract,
  [ChainName.MONAD]: commonContract,
} satisfies Record<ChainName, readonly string[]>;

const contractNames = Object.values(contractsByChain).flat();
export type ContractName = (typeof contractNames)[number];

type ContractAddressMap<ContractName extends string> = Record<
  ContractName,
  Address
>;
type ContractAddressMapByChain = {
  [ChainId.MONAD_MAINNET]: ContractAddressMap<
    (typeof contractsByChain)[ChainName.MONAD][number]
  >;
  [ChainId.MONAD_TESTNET]: ContractAddressMap<
    (typeof contractsByChain)[ChainName.MONAD_TESTNET][number]
  >;
};

const ADDRESS_BY_CHAIN: ContractAddressMapByChain = {
  [ChainId.MONAD_MAINNET]: {
    // non C deployed MultiCall3 https://github.com/mds1/multicall3, do not use for write actions
    MultiCall3: '0xcA11bde05977b3631167028862bE2a173976CA11',
    LeverUp: '0xea1b8E4aB7f14F7dCA68c5B214303B13078FC5ec',
    GenesisAirdrop: '0x1eFe02e4bd08B419989fe03bB8FA5a52BBE237D6',
    Pyth: '0x2880aB155794e7179c9eE2e38200202908C17B43',
    OneClickAgent: '0x1567FB09f13653f63A047B4F11Fb11fD1D7567d0',
    GeneralGenesisPoints: '0xAA3145E955eEd0F199D976c2BaF8297d9634fEec',
    DegenGenesisPoints: '0xF615854D3Aa3091286B7A60792d524367B5F7A0c',
    DexAggregator: '0xe465847f9C6EBd5D801257b457ab66669ea6BE09',
    LVMONRedeemer: '0xF24BED91ff0a8Fc1aCec39F6851a5eBd7dCf2BF2',
    Launchpad: FALLBACK_ADDRESS,
    LaunchpadUIHelper: FALLBACK_ADDRESS,
  },
  [ChainId.MONAD_TESTNET]: {
    MultiCall3: '0xcA11bde05977b3631167028862bE2a173976CA11',
    LeverUp: '0x1bA73F7ab65D9712D45BE5A8961a4609874f6D13',
    GenesisAirdrop: '0x10bd8BC596ACFd7a45f58ac9b6D4b57F20388B1c',
    Pyth: '0x2880aB155794e7179c9eE2e38200202908C17B43',
    OneClickAgent: '0xd9d2ccF34E80906c6e132B755b0Ed591dc4A9938',
    GeneralGenesisPoints: '0x0DC03D0A50E7cBC2F10653dcfc52A03f5715e04b',
    DegenGenesisPoints: '0xb7EF441219f7aF6D464f1a5E17733C5170211b80',
    DexAggregator: '0xFBEA181F19F3099460251078D824a68CFcAF2204',
    LVMONRedeemer: '0x2928813c11FC67Cc0aEc4c2960834E81FF97cd6F',
    Launchpad: '0xf5c76a0B9eBcD3828B1021922D7cDA9899309e8b',
    LaunchpadUIHelper: '0x407b28134b61dec741b238419a5a24ad30a76ff9',
  },
};

export const getContractAddress = (
  contractName: ContractName,
  chainId: ChainId,
): Address | undefined => {
  const result = ADDRESS_BY_CHAIN[chainId][contractName];

  return result === FALLBACK_ADDRESS ? undefined : result;
};

export const getTokenAddress = (
  symbol: TokenSymbol,
  chainId: ChainId,
): Address | undefined => {
  /** @ts-ignore */
  const result = TOKEN_ADDRESS_BY_CHAIN[chainId][symbol];

  return result === FALLBACK_ADDRESS ? undefined : result;
};
